import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Announcements,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardCode,
  KeyboardCoordinateGetter,
  UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { IHubFolder, ISidebarMoveItem } from 'types'
import { HubFolderActions, MoveResult } from 'hooks/useHubFolders'
import { SIDEBAR_ROW, SIDEBAR_ROW_PAD, SIDEBAR_SURFACE, brandText } from 'components/sidebar/surface'
import { FolderRowContent } from 'components/sidebar/components/folders/FolderRow'
import { ResourceRowContent } from 'components/sidebar/components/folders/ResourceRow'
import { SidebarNode, SidebarSort, findNode, subtreeIds } from 'components/sidebar/components/folders/tree'
import {
  DropPlan,
  RowDropIndicator,
  TREE_DROP_ID,
  describeDrop,
  dragGroup,
  dropGroups,
  dropOutcome,
  isReorder,
  manualDropFor,
  planDrop,
  planSortedDrop,
  springFolder
} from 'components/sidebar/components/folders/dnd'
import {
  Hover,
  KeyboardBand,
  PREVIEW_MODIFIERS,
  SidebarKeyboardSensor,
  SidebarPointerSensor,
  cancelDndSensors,
  collisionDetection,
  hoverFrom,
  keyboardBands,
  createSensorOptions,
  nextKeyboardStop,
  outcomePerGroup,
  sameHover,
  startDragSession
} from 'components/sidebar/components/folders/dndSession'

/** How long a drag hovers into a closed folder before it opens. */
const SPRING_DELAY_MS = 600

/** Announced when a drag is back over one of the rows it carries. */
const HOME_TEXT = 'Back where it started. Nothing will move.'

/** What the tree needs to show a drag: where it would drop, and which rows it carries. */
export interface SidebarDragView {
  dropIndicator: RowDropIndicator | null
  isDragged: (id: string) => boolean
  /** A drag is in progress. */
  dragging: boolean
}

const SCREEN_READER_INSTRUCTIONS = {
  draggable:
    'To move this item, press Alt (Option on a Mac) with the up or down arrow. Then use the arrow keys to choose where it goes, and Enter or Space to drop it, or Escape to cancel.'
}

/**
 * The tree as a drop target in its own right, behind its rows: a drop on its empty space goes to the
 * end of the top level. That's the way back to the top level in a sorted sidebar whose top level is
 * all folders. Goes inside SidebarDnd.
 */
export const SidebarDropRoot: React.FC<React.HTMLAttributes<HTMLDivElement> & { enabled: boolean }> = ({
  enabled,
  ...props
}) => {
  const { setNodeRef } = useDroppable({ id: TREE_DROP_ID, disabled: !enabled })
  return <div ref={setNodeRef} {...props} />
}

/** How a move went, for the sidebar's own live region (dnd-kit's has finished by then). */
const moveStatusText = (result: MoveResult | undefined): string => {
  if (!result || result.ok) return 'Moved.'
  return result.reason ? `Couldn't move: ${result.reason}` : 'Move failed.'
}

interface DragPreviewProps {
  node: SidebarNode
  expanded: boolean
  /** How many more items the drag carries. */
  more: number
  brandColour?: string
}

/** What follows the pointer: the row being dragged, and "+N" for the rest of the selection. */
const DragPreview: React.FC<DragPreviewProps> = ({ node, expanded, more, brandColour }) => (
  <div
    // Slightly see-through, so what's under it still shows through if it does land on a row.
    className={`${SIDEBAR_ROW} ${SIDEBAR_ROW_PAD} pointer-events-none text-body shadow-lg opacity-90`}
    style={{ backgroundColor: SIDEBAR_SURFACE }}
    data-testid="sidebar-drag-preview"
  >
    {node.kind === 'folder' ? (
      <FolderRowContent name={node.name} expanded={expanded} />
    ) : (
      <ResourceRowContent kind={node.kind} name={node.name} />
    )}
    {more > 0 ? (
      <span
        className="shrink-0 rounded-full px-1.5 text-[11px] font-semibold leading-4"
        style={{ backgroundColor: brandText(brandColour), color: SIDEBAR_SURFACE }}
      >
        +{more}
      </span>
    ) : null}
  </div>
)

interface DragState {
  activeId: string
  items: ISidebarMoveItem[]
  /** Started from the keyboard rather than a pointer. */
  keyboard: boolean
}

interface SidebarDndProps {
  /** On for a viewer who can move something. Anyone else gets the same tree with the sensors off. */
  enabled: boolean
  /** The hub on screen. A drag doesn't survive switching hub. */
  hubId: string
  /** The rows as they show, in the viewer's sort. */
  tree: SidebarNode[]
  /** The same rows in the hub's shared order, where a reorder made while sorted goes. */
  manualTree: SidebarNode[]
  folders: IHubFolder[]
  expanded: Set<string>
  /** The rows on screen this viewer may move, top to bottom: all a drag may carry. */
  visible: string[]
  /** The viewer's sort. Sorted, a drop between rows reorders the shared order and sets it back to Manual. */
  sort: SidebarSort
  /** Puts the viewer's sort back to Manual, for a reorder made while sorted. */
  onSortToManual: () => void
  selected: string[]
  brandColour?: string
  expand: (folderIds: string[]) => void
  moveItems: HubFolderActions['moveItems']
  /** A drop of the selection has started a move, so the selection is done with. */
  onDrop: () => void
  children: (drag: SidebarDragView) => React.ReactNode
}

/**
 * Drag and drop for the sidebar tree. Drag a row, or a selection, onto a folder to move it in, or
 * between rows to reorder. Hovering into a closed folder opens it. The move shows
 * at once and snaps back if the server refuses it (useHubFolders).
 *
 * The drag's state lives here and each row gets its part of it as a primitive indicator. The whole
 * tree re-renders when the drag moves onto another row or zone (and dnd-kit re-renders every
 * draggable when the row under the drag changes), so rows are kept cheap to render.
 *
 * Always rendered, with the sensors off when `enabled` is false, so a viewer who becomes a manager
 * once the hub has loaded doesn't have the whole tree remounted under them.
 */
const SidebarDnd: React.FC<SidebarDndProps> = ({
  enabled,
  hubId,
  tree,
  manualTree,
  folders,
  expanded,
  visible,
  sort,
  onSortToManual,
  selected,
  brandColour,
  expand,
  moveItems,
  onDrop,
  children
}) => {
  const [drag, setDrag] = useState<DragState | null>(null)
  const [hover, setHover] = useState<Hover | null>(null)
  // How the last move went, for screen readers.
  const [moveStatus, setMoveStatus] = useState('')
  const pointerY = useRef<number | null>(null)
  const endSession = useRef<(() => void) | null>(null)
  // For the screen-reader announcements, which dnd-kit asks for after each handler below has run:
  // the hover last seen, what dropping there would do, what was last said, and how the drop went.
  const lastHover = useRef<Hover | null>(null)
  const dropText = useRef<string | null>(null)
  const spoken = useRef<string | null>(null)
  const endText = useRef('')
  // Made once; the effect below keeps the keyboard stepping over the latest tree, and each sensor
  // on or off.
  const [sensorOptions] = useState(createSensorOptions)
  const sensors = useSensors(
    useSensor(SidebarPointerSensor, sensorOptions.pointer),
    useSensor(SidebarKeyboardSensor, sensorOptions.keyboard)
  )
  const sorted = sort !== 'manual'

  const folderIds = new Set(folders.map((f) => f.publicId))
  const isFolder = (id: string): boolean => folderIds.has(id)
  const planFor = (items: ISidebarMoveItem[], at: Hover): DropPlan | null =>
    (sorted ? planSortedDrop : planDrop)(tree, folders, items, at.overId, at.zone, expanded)
  const nameOf = (id: UniqueIdentifier): string => findNode(tree, String(id))?.name ?? 'item'

  const plan = drag && hover ? planFor(drag.items, hover) : null
  const spring = springFolder(plan, hover?.overId ?? null, expanded)

  // Re-keyed whenever the folder under the drag changes, so moving on (or dropping) cancels it.
  useEffect(() => {
    if (!spring) return
    const timer = setTimeout(() => expand([spring]), SPRING_DELAY_MS)
    return () => clearTimeout(timer)
  }, [spring])

  // A drag belongs to its hub, to a viewer who may manage folders, to the sort it began in and to
  // this sidebar: switching hub or sort, losing the permission or unmounting ends it, sensors
  // included (dnd-kit leaves them running otherwise, and turning the sensors off doesn't stop one
  // already started).
  useEffect(
    () => () => {
      if (!endSession.current) return
      cancelDndSensors()
      endSession.current?.()
      endSession.current = null
    },
    [hubId, enabled, sort]
  )

  const finish = (): void => {
    endSession.current?.()
    endSession.current = null
    pointerY.current = null
    lastHover.current = null
    dropText.current = null
    spoken.current = null
    setDrag(null)
    setHover(null)
  }

  const describeHover = (at: Hover | null): string => {
    if (!drag || !at) return 'Not over anywhere to drop.'
    if (drag.items.some((item) => item.id === at.overId)) return HOME_TEXT
    return describeDrop(planFor(drag.items, at), nameOf)
  }

  // Called on every move; only a change of row or zone does any work.
  const updateHover = (event: DragMoveEvent): void => {
    const next = hoverFrom(event, pointerY.current, isFolder)
    if (sameHover(lastHover.current, next)) return
    lastHover.current = next
    setHover(next)
    dropText.current = describeHover(next)
  }

  /** Says what a drop would now do, once per change, so a keyboard user hears each stop. */
  const announceDrop = (): string | undefined => {
    if (dropText.current === spoken.current) return undefined
    spoken.current = dropText.current
    return dropText.current ?? undefined
  }

  /** Where a keyboard drag's band would drop, as dropOutcome's key; the drag's own rows are `none`. */
  const outcomeOf = (band: KeyboardBand): string => {
    if (!drag || drag.items.some((item) => item.id === band.overId)) return 'none'
    return dropOutcome(tree, drag.items, planFor(drag.items, band))
  }

  // Each arrow press goes to the middle of the next band that drops somewhere new.
  const step: KeyboardCoordinateGetter = (event, { currentCoordinates, context }) => {
    const direction = event.code === KeyboardCode.Down ? 1 : event.code === KeyboardCode.Up ? -1 : 0
    const rect = context.collisionRect
    if (direction === 0 || !rect) return undefined
    const centre = rect.top + rect.height / 2
    const rows = [...context.droppableRects]
      .filter(([id]) => id !== TREE_DROP_ID)
      .map(([id, row]) => ({
        id: String(id),
        top: row.top,
        height: row.height,
        isFolder: isFolder(String(id))
      }))
    const outcomes = outcomePerGroup(outcomeOf, dropGroups(tree, drag?.items ?? []))
    const stop = nextKeyboardStop(keyboardBands(rows), centre, direction, outcomes)
    return stop ? { ...currentCoordinates, y: currentCoordinates.y + stop.y - centre } : undefined
  }
  useEffect(() => {
    sensorOptions.setStep(step)
    sensorOptions.setGate({ pointer: enabled, keyboard: enabled })
  })

  const onDragStart = ({ active, activatorEvent }: DragStartEvent): void => {
    const activeId = String(active.id)
    endSession.current = startDragSession(activatorEvent, (y) => {
      pointerY.current = y
    })
    // A drag starts over its own row: nothing to add to "Picked up …" until it moves off.
    dropText.current = HOME_TEXT
    spoken.current = HOME_TEXT
    setMoveStatus('')
    setDrag({
      activeId,
      items: dragGroup(tree, folders, activeId, selected, visible),
      keyboard: activatorEvent instanceof KeyboardEvent
    })
  }

  // Worked out again from the drop itself, in case the last move hasn't rendered yet.
  const onDragEnd = (event: DragEndEvent): void => {
    const end = hoverFrom(event, pointerY.current, isFolder)
    const dropped = drag && end ? planFor(drag.items, end) : null
    finish()
    if (!drag || !dropped?.indicator) {
      endText.current = 'Dropped. Nothing moved.'
      return
    }
    endText.current = `Moving… ${describeDrop(dropped, nameOf)}`
    // A reorder while sorted goes beside the same row in the shared order, which the sidebar goes
    // back to showing.
    const reordered = sorted && isReorder(dropped)
    const placed = reordered ? manualDropFor(manualTree, folders, drag.items, dropped, expanded) : dropped
    if (reordered) {
      onSortToManual()
      endText.current += ' Sort set to Manual.'
    }
    // Already beside that row in the shared order: going back to it is the whole move.
    if (!placed?.indicator) return
    const { parentFolderId, index } = placed.target
    // Into a closed folder from the keyboard: open it, so the row stays on screen and dnd-kit can
    // hand focus back to it there.
    if (drag.keyboard && parentFolderId && !expanded.has(parentFolderId)) expand([parentFolderId])
    // A drag of an unselected row leaves the selection alone.
    if (selected.includes(drag.activeId)) onDrop()
    moveItems(drag.items, parentFolderId, index).then((result) => setMoveStatus(moveStatusText(result)))
  }

  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      // Announced before the drag's state has rendered, so the group is worked out afresh.
      const more = dragGroup(tree, folders, String(active.id), selected, visible).length - 1
      return `Picked up ${nameOf(active.id)}${more > 0 ? ` and ${more} more` : ''}.`
    },
    onDragMove: announceDrop,
    onDragOver: announceDrop,
    onDragEnd: () => endText.current,
    onDragCancel: () => 'Move cancelled. Nothing moved.'
  }

  // A dragged folder carries everything inside it, so all of that is dimmed with it.
  const draggedIds = new Set(
    drag?.items.flatMap((item) => {
      const node = findNode(tree, item.id)
      return node ? subtreeIds(node) : [item.id]
    })
  )
  // The preview shows the row under the pointer, unless it's travelling inside a dragged folder.
  const lead = drag ? (drag.items.find((item) => item.id === drag.activeId) ?? drag.items[0]) : undefined
  const leadNode = lead ? findNode(tree, lead.id) : undefined

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      accessibility={{
        announcements,
        screenReaderInstructions: SCREEN_READER_INSTRUCTIONS
      }}
      onDragStart={onDragStart}
      onDragMove={updateHover}
      onDragOver={updateHover}
      onDragEnd={onDragEnd}
      onDragCancel={finish}
    >
      {children({
        dropIndicator: plan?.indicator ?? null,
        isDragged: (id) => draggedIds.has(id),
        dragging: drag !== null
      })}
      <div role="status" aria-live="polite" className="sr-only" data-testid="sidebar-move-status">
        {moveStatus}
      </div>
      {/* In the body, so the sidebar's own clipping and stacking don't cut the preview off. Offset
          (PREVIEW_MODIFIERS) so it doesn't cover the ring or line it would drop on. No drop
          animation: it would fly back to where the row was, which reads as the move failing. */}
      {createPortal(
        <DragOverlay dropAnimation={null} modifiers={PREVIEW_MODIFIERS}>
          {drag && leadNode ? (
            <DragPreview
              node={leadNode}
              expanded={expanded.has(leadNode.id)}
              more={drag.items.length - 1}
              brandColour={brandColour}
            />
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  )
}

export default SidebarDnd
